/*----------------------------------
- CATALOG (SSOT): HOME COPY
----------------------------------*/

/**
 * Home's words outside the cards, the funnel and the demo's tabs (docs/mockups/website-home-v3.html, read with
 * .claude/rules/website-copy.md): the hero, the Find a name panel of the demo, the closing card, the FAQ head, the Ask
 * AI line (the footer sky's sentence is `WORLD_FOOTER_COPY.sub`). Figures are never here; the page passes the server's.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule The h1 keeps the shipped wording (plan settled 14). Calls to action read "Start with my search" everywhere on
 *       Home except the header's "Start my search"; "brief" is never a noun; "rating", never "verdict".
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { HomePersona } from '@uniquedomains/components/marketing/demo-window/PersonaSwitch';

import { START_WITH_SEARCH_LABEL } from '@/client/catalogs/landing/accessOffer';
import { WORLD_FOOTER_COPY } from '@/client/catalogs/landing/worldFooter';
import type { SpriteIconName } from '@/client/components/ui/icon/Icon';

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const HOME_HERO_COPY = {
    /** The h1's three lines, one sentence each (Gaetan, 2026-10-25). */
    title: { lead: 'One-word domains.', middle: 'Search them all.', payoff: 'Buy the right one.' },
    /**
     * The shorter description of Home v3 (2026-11-04), in three parts: the mock keeps "one-word" on one line
     * (`span.nw`), so the page wraps the middle part in a no-wrap span. "millions" is the mock's word, not a figure.
     */
    sub: {
        before: 'Describe what you want and search millions of ',
        nowrap: 'one-word',
        after: " domains. Check any name's price, value and trademark risk. Saved searches send new matches daily.",
    },
    /** The persona pair as drawn in v3: the white soft investor pill, the glass founder pill. */
    personaCtas: { investor: "I'm an investor", founder: "I'm a founder" } satisfies Record<HomePersona, string>,
    /** After the live members count (`ProofLine`): the world footer's words, read from its catalog (review m-B12). */
    proofLabel: WORLD_FOOTER_COPY.proofLabel,
} as const;

/** One filter group of the demo's Browse rail: every row opens the wall of the whole search. */
export type HomeBrowseGroup = { label: string; icon: SpriteIconName };

export const HOME_BROWSE_COPY = {
    modes: ['Domains', 'Words'],
    /** The table's region name and the reset control beside the mode switch, as drawn. */
    region: "Today's names",
    reset: 'Reset the search',
    searchLabel: 'Describe the names you need',
    deepSearch: 'Deep Search',
    /** After the catalog size in the Deep Search row ("32.2M names"). */
    namesUnit: 'names',
    tab: 'Search 1',
    newTab: 'New search tab',
    filters: 'Filters',
    export: 'Export',
    share: 'Share',
    radar: 'Radar on this search',
    groups: [
        { label: 'Identity', icon: 'sparkles' },
        { label: 'Status', icon: 'clock' },
        { label: 'Price', icon: 'tag' },
        { label: 'Value', icon: 'coins' },
        { label: 'Endings', icon: 'globe' },
        { label: 'Demand', icon: 'search' },
        { label: 'History', icon: 'archive' },
        { label: 'Legal', icon: 'shield' },
        { label: 'Signals', icon: 'radio' },
        { label: 'Mine', icon: 'star' },
    ] satisfies readonly HomeBrowseGroup[],
    /** The strip under the rows (default 6: the four figures Product serves for the search, `homeDemoStrip.ts`). */
    strip: {
        label: 'This search',
        open: (pct: number) => `${pct}% open`,
        aftermarket: (pct: number) => `${pct}% aftermarket`,
        medianAsk: 'median ask',
    },
    /** The kit EmptyState when the cached search is not renderable (the shipped page hid the section). */
    unready: {
        title: "Today's names are on their way",
        text: 'The list refreshes every few minutes. Every one-word domain is on the full list now.',
        action: 'See the domains',
    },
} as const;

/** The floating dock that switches the hero picture (Home v3, 2026-11-04), as the mock names it and its two buttons. */
export const HOME_HERO_DOCK_COPY = {
    label: 'Hero options',
    day: 'Day station',
    night: 'Night launch',
} as const;

/**
 * The closing card since Home v3 (2026-11-04): the ask, the call to action, and both dashboards in one window under a
 * drag slider (the persona switch and its line left the card). `compare` names the two views and the slider.
 */
export const HOME_CLOSING_COPY = {
    heading: 'Ready to see your first matches?',
    sub: 'Describe your search and pick how you hunt. Your first names come back in about two minutes with their price, renewal, demand and trademark risk, and new ones land in your workspace every morning.',
    cta: START_WITH_SEARCH_LABEL,
    compare: {
        views: { investor: 'Investor view', founder: 'Founder view' } satisfies Record<HomePersona, string>,
        hint: 'Drag to compare',
        label: "Slide between the investor's dashboard and the founder's",
    },
} as const;

export const HOME_FEATURE_CARDS_CTA = START_WITH_SEARCH_LABEL;

export const HOME_FAQ_HEAD = {
    title: 'Questions people ask before they trust a domain tool.',
    lead: 'Direct answers about one-word domains, what a report proves, and what it costs.',
} as const;

export const HOME_ASK_AI_COPY = {
    title: 'Ask AI about Unique Domains',
    /** The prompt's page title, as the shipped Home section sent it, so the assistants' answers stay comparable. */
    promptPageTitle: 'Unique Domains homepage',
    providerTitle: (provider: string) => `Ask ${provider} about Unique Domains`,
} as const;
